import { useCallback } from 'react';
import type { FormError } from '@web-config-app/validation';
import { useIntl } from '@leagueplatform/locales';
import {
  EntityDetailError,
  findClosestAncestorPath,
  instancePathToFormPath,
} from '@web-config-app/core';
import { useEntityDetailsProps } from '../use-entity-details-props/use-entity-details-props';

type FormatMessage = ReturnType<typeof useIntl>['formatMessage'];

const getDetail = ({
  error,
  formatMessage,
}: {
  error: FormError;
  formatMessage: FormatMessage;
}) => {
  if (error.message) {
    return error.propertyName
      ? `${formatMessage({ id: error.propertyName })} ${error.message}`
      : error.message;
  }
  return `${error.keyword} error: ${error.schemaPath}`;
};

/**
 * This hook constructs a ConfigError
 * uses the `error.propertyName` (if available) to build the error detail.
 *
 * @returns {function} A memoized callback function that takes an array of `FormError` objects and returns a `ConfigError` object.
 */
export const useFormatFormErrors = (): ((
  formErrors: FormError[],
) => EntityDetailError[]) => {
  const { formatMessage } = useIntl();
  const { nodeMap } = useEntityDetailsProps();

  // if true, errors for the top level parentNode are grouped into a single message with the error amount
  const shouldGroupErrors = useCallback(
    (nodeId: string) => {
      const schema = nodeMap.get(nodeId)?.data?.schema;
      // Group errors for flow type nodes as each flow node is flattened and opens a modal so there is only one page route for all the errors.
      return schema?.['x-entity-control']?.type === 'flow';
    },
    [nodeMap],
  );

  return useCallback(
    (formErrors: FormError[]) => {
      const collectedErrors: EntityDetailError[] = [];
      const groupedErrorDetails = new Map<
        string,
        { count: number; label: string; index: number }
      >();

      formErrors.forEach((error) => {
        // Client-side Ajv errors carry real instancePaths into the
        // form's data — ancestor-walking is sufficient. Skip the
        // property-name fallback that `findNavigableFormPath` adds
        // (it's for abbreviated BE pointers); for client-side errors
        // a guessed link to an unrelated section would be misleading.
        const nodeId = findClosestAncestorPath(
          instancePathToFormPath(error.instancePath),
          (path) => nodeMap.has(path),
        );
        const isGroupError = nodeId ? shouldGroupErrors(nodeId) : false;
        if (isGroupError && nodeId) {
          const itemSchema = nodeMap.get(nodeId)?.data?.schema as
            | Record<string, any>
            | undefined;
          const label =
            itemSchema?.['x-entity-label']?.text ||
            formatMessage({ id: itemSchema?.['x-entity-label']?.key });

          const existing = groupedErrorDetails.get(nodeId);
          if (existing) {
            existing.count += 1;
            // Update existing error message in place
            collectedErrors[existing.index] = {
              detail: formatMessage(
                { id: 'GROUP_ERRORS' },
                { count: existing.count, label },
              ),
              path: nodeId,
            };
          } else {
            const index = collectedErrors.length;
            groupedErrorDetails.set(nodeId, { count: 1, label, index });
            collectedErrors.push({
              detail: `${label} has 1 error`,
              path: nodeId,
            });
          }
        } else {
          collectedErrors.push({
            detail: getDetail({ error, formatMessage }),
            path: nodeId,
          });
        }
      });
      return collectedErrors;
    },
    [formatMessage, nodeMap, shouldGroupErrors],
  );
};
